import { auth } from "../firebase/config";

/**
 * Cloudflare D1 API Client
 * Connects frontend user data, reading records, notes, and admin ops to Cloudflare D1.
 */

const API_BASE = process.env.REACT_APP_API_URL || "/api";
const inflightRequests = new Map();

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getAuthToken = async () => {
  try {
    if (auth && auth.currentUser && typeof auth.currentUser.getIdToken === "function") {
      return await auth.currentUser.getIdToken();
    }
  } catch (e) {
    // Ignore error if offline or token fetch fails
  }
  return null;
};

export const waitForAuthToken = async (timeoutMs = 4000) => {
  const started = Date.now();
  let token = await getAuthToken();
  if (token) return token;
  while (Date.now() - started < timeoutMs) {
    if (!auth?.currentUser) return null;
    await sleep(50);
    token = await getAuthToken();
    if (token) return token;
  }
  return null;
};

const request = async (endpoint, options = {}) => {
  const {
    public: isPublic = false,
    requireAuth = !isPublic,
    cache = "no-store",
    headers: extraHeaders,
    signal,
    ...fetchOptions
  } = options;
  const url = `${API_BASE}${endpoint}`;
  const method = String(fetchOptions.method || "GET").toUpperCase();
  const dedupeKey = `${method}:${url}:${fetchOptions.body || ""}`;

  if (inflightRequests.has(dedupeKey)) {
    return inflightRequests.get(dedupeKey);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  const pending = (async () => {
    try {
      const token = requireAuth ? await waitForAuthToken() : await getAuthToken();
      if (requireAuth && !token) {
        throw new Error("Not signed in");
      }

      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(extraHeaders || {}),
      };

      const res = await fetch(url, {
        ...fetchOptions,
        method,
        headers,
        cache,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `D1 API Error: ${res.status} ${res.statusText}`);
      }

      return await res.json();
    } catch (err) {
      clearTimeout(timeoutId);
      console.warn(`[Cloudflare D1 Client] Request to ${url} failed:`, err.message);
      throw err;
    } finally {
      inflightRequests.delete(dedupeKey);
    }
  })();

  inflightRequests.set(dedupeKey, pending);
  return pending;
};

// ==========================================
// 1. User Profiles
// ==========================================
export const d1GetUserProfile = async (uid) => {
  try {
    return await request(`/users/${encodeURIComponent(uid)}`);
  } catch (e) {
    return null;
  }
};

export const d1UpsertUserProfile = async (uid, data) => {
  return await request(`/users/${encodeURIComponent(uid)}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const d1UpdateUserProfile = async (uid, data) => {
  return await request(`/users/${encodeURIComponent(uid)}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

// ==========================================
// 2. Reading Records
// ==========================================
export const d1GetReadingRecords = async (uid) => {
  try {
    return await request(`/reading/${encodeURIComponent(uid)}`);
  } catch (e) {
    return null;
  }
};

export const d1SaveReadingRecords = async (uid, payload) => {
  return await request(`/reading/${encodeURIComponent(uid)}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

// ==========================================
// 3. Notes
// ==========================================
export const d1GetUserNotes = async (userId) => {
  return await request(`/notes?userId=${encodeURIComponent(userId)}`);
};

export const d1GetChapterNotes = async (chapterId, userId) => {
  let q = `/notes?chapterId=${encodeURIComponent(chapterId)}`;
  if (userId) {
    q += `&userId=${encodeURIComponent(userId)}`;
  }
  return await request(q);
};

export const d1GetSharedNotes = async ({ max = 50, chapterId = "", bypassCache = false } = {}) => {
  const chapterQuery = chapterId ? `&chapterId=${encodeURIComponent(chapterId)}` : "";
  const bust = bypassCache ? `&_ts=${Date.now()}` : "";
  return await request(`/notes/shared?max=${max}${chapterQuery}${bust}`, { public: true, cache: "no-store" });
};

export const d1GetVerseNoteCounts = async (chapterId) => {
  if (!chapterId) return { chapterId: "", counts: {}, publicCounts: {}, myCounts: {} };
  return await request(`/notes/verse-counts?chapterId=${encodeURIComponent(chapterId)}`, {
    public: true,
    cache: "no-store",
  });
};

export const d1RequestNotePublic = async (id) => {
  return await request(`/notes/${encodeURIComponent(id)}/request-public`, {
    method: "POST",
  });
};

export const d1WithdrawNotePublic = async (id) => {
  return await request(`/notes/${encodeURIComponent(id)}/withdraw-public`, {
    method: "POST",
  });
};

export const d1GetNoteById = async (id) => {
  return await request(`/notes/${encodeURIComponent(id)}`);
};

export const d1CreateNote = async (noteData) => {
  return await request("/notes", {
    method: "POST",
    body: JSON.stringify(noteData),
  });
};

export const d1UpdateNote = async (id, noteData) => {
  return await request(`/notes/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(noteData),
  });
};

export const d1DeleteNote = async (id) => {
  return await request(`/notes/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
};

// ==========================================
// 4. Admin Management
// ==========================================
export const d1GetAdminUsers = async () => {
  return await request("/admin/users");
};

export const d1UpdateUserRole = async (uid, role) => {
  return await request(`/admin/users/${encodeURIComponent(uid)}/role`, {
    method: "POST",
    body: JSON.stringify({ role }),
  });
};

export const d1UpdateUserStatus = async (uid, status) => {
  return await request(`/admin/users/${encodeURIComponent(uid)}/status`, {
    method: "POST",
    body: JSON.stringify({ status }),
  });
};

export const d1GetAdminNotes = async () => {
  return await request("/admin/notes");
};

export const d1GetPendingAdminNotes = async () => {
  return await request("/admin/notes/pending");
};

export const d1ReviewNote = async (id, { action, reason = "" } = {}) => {
  return await request(`/admin/notes/${encodeURIComponent(id)}/review`, {
    method: "POST",
    body: JSON.stringify({ action, reason }),
  });
};

export const d1ModerateNote = async (id, updates) => {
  return await request(`/admin/notes/${encodeURIComponent(id)}/moderate`, {
    method: "POST",
    body: JSON.stringify(updates),
  });
};

export const d1AdminDeleteNote = async (id) => {
  return await request(`/notes/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
};

export const d1GetAdminStats = async () => {
  return await request("/admin/stats");
};

// ==========================================
// 5. Friends & User Search
// ==========================================
export const d1SearchUsers = async (query) => {
  if (!query || query.trim().length < 2) return [];
  try {
    return await request(`/users/search?q=${encodeURIComponent(query.trim())}`);
  } catch (e) {
    return [];
  }
};

export const d1GetFriends = async () => {
  try {
    return await request("/friends");
  } catch (e) {
    return [];
  }
};

export const d1GetFriendRequests = async () => {
  try {
    return await request("/friends/requests");
  } catch (e) {
    return { incoming: [], outgoing: [] };
  }
};

export const d1SendFriendRequest = async (arg1, arg2) => {
  let targetUid = null;
  let targetEmail = null;
  if (typeof arg1 === "object" && arg1 !== null) {
    targetUid = arg1.targetUid;
    targetEmail = arg1.targetEmail;
  } else {
    targetUid = arg1;
    targetEmail = arg2;
  }
  return await request("/friends/request", {
    method: "POST",
    body: JSON.stringify({ targetUid, targetEmail }),
  });
};

export const d1RespondFriendRequest = async (arg1, arg2, arg3) => {
  let requestId = null;
  let action = null;
  let targetUid = null;
  if (typeof arg1 === "object" && arg1 !== null) {
    requestId = arg1.requestId;
    action = arg1.action;
    targetUid = arg1.targetUid;
  } else {
    requestId = arg1;
    action = arg2;
    targetUid = arg3;
  }
  return await request("/friends/respond", {
    method: "POST",
    body: JSON.stringify({ requestId, targetUid, action }),
  });
};

export const d1RemoveFriend = async (targetUid) => {
  return await request(`/friends/${encodeURIComponent(targetUid)}`, {
    method: "DELETE",
  });
};

// ==========================================
// 6. Direct Messages
// ==========================================
export const d1GetNotificationsCount = async () => {
  try {
    return await request("/messages/unread-count");
  } catch (e) {
    return { unreadMessages: 0, pendingRequests: 0, totalNotifications: 0 };
  }
};

export const d1GetUnreadCounts = d1GetNotificationsCount;

export const d1GetConversations = async () => {
  try {
    return await request("/messages/conversations");
  } catch (e) {
    return [];
  }
};

export const d1GetMessages = async (otherUid) => {
  return await request(`/messages/${encodeURIComponent(otherUid)}`);
};

export const d1SendMessage = async (otherUid, arg2, arg3) => {
  let content = null;
  let verseQuote = null;
  if (typeof arg2 === "object" && arg2 !== null) {
    content = arg2.content;
    verseQuote = arg2.verseQuote;
  } else {
    content = arg2;
    verseQuote = arg3;
  }
  return await request(`/messages/${encodeURIComponent(otherUid)}`, {
    method: "POST",
    body: JSON.stringify({ content, verseQuote }),
  });
};

export const d1MarkMessagesRead = async (otherUid) => {
  return await request(`/messages/${encodeURIComponent(otherUid)}/read`, {
    method: "POST",
  });
};
